/**
 * Line diff for the prompt editor: what a draft changes against its base
 * version. The LLM change summary says *why*; this says exactly *what*.
 *
 * Plain LCS over lines — prompt bodies run to a few thousand lines at most,
 * so the O(n·m) table is fine. Unchanged runs longer than `context` lines on
 * each side of a change collapse into a single "skip" row.
 */

export type DiffLine =
  | { kind: "same" | "added" | "removed"; text: string }
  | { kind: "skip"; count: number };

export function diffLines(before: string, after: string, context = 3): DiffLine[] {
  const a = before === "" ? [] : before.split("\n");
  const b = after === "" ? [] : after.split("\n");
  const n = a.length;
  const m = b.length;
  // lcs[i][j] = LCS length of a[i:] and b[j:]
  const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const full: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      full.push({ kind: "same", text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      full.push({ kind: "removed", text: a[i] });
      i += 1;
    } else {
      full.push({ kind: "added", text: b[j] });
      j += 1;
    }
  }
  while (i < n) full.push({ kind: "removed", text: a[i++] });
  while (j < m) full.push({ kind: "added", text: b[j++] });
  return collapse(full, context);
}

function collapse(lines: DiffLine[], context: number): DiffLine[] {
  const changed = lines.map((line) => line.kind !== "same");
  const keep = lines.map((_, index) => {
    for (let k = Math.max(0, index - context); k <= Math.min(lines.length - 1, index + context); k += 1) {
      if (changed[k]) return true;
    }
    return false;
  });
  const out: DiffLine[] = [];
  let skipped = 0;
  lines.forEach((line, index) => {
    if (keep[index]) {
      if (skipped) out.push({ kind: "skip", count: skipped });
      skipped = 0;
      out.push(line);
    } else {
      skipped += 1;
    }
  });
  if (skipped) out.push({ kind: "skip", count: skipped });
  return out;
}

export function hasChanges(lines: DiffLine[]): boolean {
  return lines.some((line) => line.kind === "added" || line.kind === "removed");
}
